import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { ProposalByProperty } from "rsl-api-106/lib/types";

import LoadingBar from "@components/_Layout/Loaders/LoadingBar";
import SearchResultListLoadingShimmer from "@components/_Layout/Loaders/LoadingShimmer/SearchResultListLoadingShimmer";
import ProposalList from "@components/Proposals/ProposalList";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, Transition } from "@headlessui/react";
import { useConfig, useSearch, useSession } from "@hooks";
import { sortBy } from "@utils/arrays";

type SearchResultListProps = {
  //
};

const languagesVarsCodes = [
  "LANG_neobooking_search_result_count", //résultats trouvés pour votre recherche.
  "LANG_neobooking_search_no_result", // Aucun résultat ne correspond à votre recherche !
  "LANG_neobooking_search_order_by", // Trier par:
];

const SearchResultList: React.FC<SearchResultListProps> = () => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { proposals, isSearching } = useSearch();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const themeTextClass = mode === "vacanceole" ? "tw-text-v_blue-dark" : "tw-text-primary_corseole-500";

  /* State */
  const [showProgress, setShowProgress] = useState<boolean>(true);
  const [sortedEtabProposals, setSortedEtabProposals] = useState<
    ProposalByProperty[]
  >([]);

  useEffect(() => {
    if (proposals) {
      setSortedEtabProposals([...proposals] as ProposalByProperty[]);
    }
  }, [proposals]);

  useEffect(() => {
    setShowProgress(isSearching);
  }, [isSearching]);

  const getNbTotalEtabProposals = (
    proposalsByEtab: ProposalByProperty[]
  ): number => {
    let result: number = 0;
    proposalsByEtab.forEach((x) => {
      result += x.proposals?.length ?? 0;
    });
    return result;
  };

  return (
    <>
      {isSearching ? (
        <>
          {showProgress && <LoadingBar variant="primary" />}
          <SearchResultListLoadingShimmer rows={5} />
        </>
      ) : (
        <>
          {sortedEtabProposals && sortedEtabProposals.length > 0 ? (
            <>
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-font-montserrat">
                <div className={`tw-font-semibold ${themeTextClass}`}>
                  {getNbTotalEtabProposals(proposals as ProposalByProperty[])}{" "}
                  {getLanguageVarValue("LANG_neobooking_search_result_count")}
                </div>
                <div className="tw-hidden">
                  <SortByMenu
                    label={getLanguageVarValue(
                      "LANG_neobooking_search_order_by"
                    )}
                    initialEtabProposals={
                      JSON.parse(
                        JSON.stringify(proposals)
                      ) as ProposalByProperty[]
                    }
                    setSortedEtabProposals={setSortedEtabProposals}
                  />
                </div>
              </div>
              <ProposalList
                proposalsByEtab={sortedEtabProposals as ProposalByProperty[]}
              />
            </>
          ) : (
            <>
              <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-gray-100 tw-rounded-[26px] tw-shadow-sm tw-py-10 tw-px-6 tw-text-center tw-font-montserrat">
                <span className={`tw-block tw-font-semibold ${themeTextClass}`}>
                  {getLanguageVarValue("LANG_neobooking_search_no_result")}
                </span>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

type SortByType = {
  code: "pertinence" | "price_asc" | "price_dsc" | "name";
  label: string;
};

const sorts: SortByType[] = [
  {
    code: "pertinence",
    label: "Pertinence",
  },
  {
    code: "price_asc",
    label: "Prix ascendant",
  },
  {
    code: "price_dsc",
    label: "Prix descendant",
  },
  {
    code: "name",
    label: "Nom de l'établissement",
  },
];

type SortByMenuProps = {
  initialEtabProposals: ProposalByProperty[];
  setSortedEtabProposals: (sortedEtabProposals: ProposalByProperty[]) => void;
  label: string;
};

const SortByMenu: React.FC<SortByMenuProps> = ({
  initialEtabProposals,
  setSortedEtabProposals,
  label,
}) => {
  const [selectedSortBy, setSelectedSortBy] = useState<SortByType>(sorts[0]);

  const handleSortBy = (code: SortByType["code"]) => {
    if (selectedSortBy.code !== code) {
      const sort = sorts.find((x) => x.code === code)
        ? sorts.find((x) => x.code === code)!!
        : sorts[0];
      const _etabProposals = [...initialEtabProposals];
      switch (sort.code) {
        case "pertinence":
          setSortedEtabProposals(_etabProposals);
          break;
        case "name":
          setSortedEtabProposals(
            sortBy(_etabProposals, (x) => x.property?.label!!)
          );
          break;

        case "price_asc":
          let sortedByPriceAscEtabProposals: ProposalByProperty[] = [];
          _etabProposals.forEach((etabProposal) => {
            let sortedProposal = sortBy(
              etabProposal.proposals,
              (x) => x.price?.amount!!
            );
            sortedByPriceAscEtabProposals = [
              ...sortedByPriceAscEtabProposals,
              {
                property: etabProposal.property,
                proposals: sortedProposal,
              },
            ];
          });
          setSortedEtabProposals(sortedByPriceAscEtabProposals);
          break;

        case "price_dsc":
          let sortedByPriceDescEtabProposals: ProposalByProperty[] = [];
          _etabProposals.forEach((etabProposal) => {
            let sortedProposal = sortBy(
              etabProposal.proposals,
              (x) => x.price?.amount!!
            ).reverse();
            sortedByPriceDescEtabProposals = [
              ...sortedByPriceDescEtabProposals,
              {
                property: etabProposal.property,
                proposals: sortedProposal,
              },
            ];
          });
          setSortedEtabProposals(sortedByPriceDescEtabProposals);
          break;

        default:
          setSortedEtabProposals(_etabProposals);
          break;
      }

      setSelectedSortBy(sort);
    }
  };

  return (
    <>
      <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
        <div>
          <Menu.Button className="tw-inline-flex tw-items-center tw-gap-2 tw-justify-center tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-font-medium focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white focus-visible:tw-ring-opacity-75">
            {label} {selectedSortBy.label}
            <FontAwesomeIcon icon={faChevronDown} />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="tw-transition tw-ease-out tw-duration-100"
          enterFrom="tw-transform tw-opacity-0 tw-scale-95"
          enterTo="tw-transform tw-opacity-100 tw-scale-100"
          leave="tw-transition tw-ease-in tw-duration-75"
          leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
          leaveTo="tw-transform tw-opacity-0 tw-scale-95"
        >
          <Menu.Items className="tw-absolute tw-right-0 tw-w-56 tw-mt-2 tw-origin-top-right tw-bg-white tw-divide-y tw-divide-gray-100 tw-rounded-md tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none">
            <div className="">
              {sorts.map((sort) => {
                return (
                  <Menu.Item key={`sort_${sort.code}`}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => handleSortBy(sort.code)}
                        className={`${active ? "tw-bg-gray-100" : ""} ${
                          sort.code === selectedSortBy.code &&
                          "tw-text-primary-500"
                        } tw-group tw-flex tw-rounded-t tw-items-center tw-w-full tw-px-2 tw-py-2 tw-text-sm`}
                      >
                        {sort.label}
                      </button>
                    )}
                  </Menu.Item>
                );
              })}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </>
  );
};

SearchResultList.propTypes = {
  //
};

export default SearchResultList;
